import { ChevronRight } from 'lucide-react';
import { useState } from 'react';
import Button from '../../common/Button';
import Card from '../../common/Card';
import {
  MemberDTO,
  MemberTypeDTO,
  MemberFeeAmountDTO,
} from '../../../services/apiClient';
import { MemberRegistrationStepConstants } from '../../../constants/MemberRegistrationStepConstants';
import { useMemberApi } from '../../../hooks/useMemberApi';
import { MemberCategoryConstants } from '../../../constants/MemberCategoryConstants';

interface MembershipProps {
  member: MemberDTO | undefined;
  memberType: MemberTypeDTO | undefined;
  memberTypeFee: MemberFeeAmountDTO | undefined;
  onNext: () => void;
  // Called when the server rejects the step change (e.g. registration already completed in another tab)
  onStale?: () => void;
  isCalculatingFees?: boolean;
}

const Membership: React.FC<MembershipProps> = ({
  member,
  memberType,
  memberTypeFee,
  onNext,
  onStale,
  isCalculatingFees = false,
}) => {
  const { updateRegistrationStep } = useMemberApi();
  const [isUpdating, setIsUpdating] = useState(false);
  const isMemberTypeReady = Boolean(memberType?.id);

  const handleMembershipSelect = async (optionId: string) => {
    setIsUpdating(true);
    try {
      const memberTypeId = memberType?.id;

      if (!memberTypeId) {
        console.error('Member type is not yet available');
        return;
      }

      if (member && member.id)
        await updateRegistrationStep(member.id, {
          memberRegistrationStepId: MemberRegistrationStepConstants.PAYMENT,
          memberTypeId,
          memberCategoryId: optionId,
        });

      onNext();
    } catch (error) {
      console.error('Error updating member', error);
      onStale?.();
    } finally {
      setIsUpdating(false);
    }
  };

  const membershipOptions = [
    {
      id: MemberCategoryConstants.OCCASIONAL_HUNTER,
      title: 'Hunter',
      icon: (
        <img
          src="/images/Icons_Apricot_Bokkie_Kop.png"
          alt="Hunter"
          className="w-20 h-20 object-contain"
        />
      ),
      price: memberTypeFee?.fee,
      joinFee: memberTypeFee?.joinFee,
      total: (memberTypeFee?.fee || 0) + (memberTypeFee?.joinFee || 0),
    },
    {
      id: MemberCategoryConstants.OCCASIONAL_SPORTPERSON,
      title: 'Sport-person',
      icon: (
        <img
          src="/images/Icons_Apricot_Archery_Target.png"
          alt="Hunter"
          className="w-20 h-20 object-contain"
        />
      ),
      price: memberTypeFee?.fee,
      joinFee: memberTypeFee?.joinFee,
      total: (memberTypeFee?.fee || 0) + (memberTypeFee?.joinFee || 0),
    },
    {
      id: MemberCategoryConstants.OCCASIONAL_HUNTER_AND_SPORTPERSON,
      title: 'Hunter and Sport-person',
      icon: (
        <div className="flex items-center gap-1">
          <img
            src="/images/Icons_Apricot_Bokkie_Kop.png"
            alt="Hunter"
            className="w-20 h-20 object-contain"
          />
          <img
            src="/images/Icons_Apricot_Archery_Target.png"
            alt="Hunter"
            className="w-20 h-20 object-contain"
          />
        </div>
      ),
      price: memberTypeFee?.fee,
      joinFee: memberTypeFee?.joinFee,
      total: (memberTypeFee?.fee || 0) + (memberTypeFee?.joinFee || 0),
    },
  ];

  return (
    <Card>
      <div className="flex items-center justify-center">
        <div className="max-w-4xl w-full p-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-700 mb-2">
              Choose a membership below
            </h1>
            <h2 className="text-xl text-gray-600 mb-10">
              Annual Membership ({memberType?.titleShort})
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {membershipOptions.map((option) => (
              <div
                key={option.id}
                className="bg-white border-2 border-gray-200 rounded-xl p-6 text-center"
              >
                <div className="flex justify-center mb-4">{option.icon}</div>

                <h3 className="text-lg font-semibold text-gray-700 mb-6 h-14">
                  {option.title}
                </h3>

                <div className="space-y-2 mb-6">
                  <div className="text-3xl font-bold text-primary">
                    R {option.price}
                  </div>
                  <div className="text-gray-500 italic">per year</div>
                  {option.joinFee != null && option.joinFee > 0 && (
                    <>
                      <div className="text-2xl text-gray-600 font-bold">+</div>
                      <div className="text-gray-500">
                        R {option.joinFee} joining fee
                      </div>
                      <div className="text-gray-500 italic">once off</div>
                    </>
                  )}
                  <div className="border-t pt-2 mt-2">
                    <div className="font-semibold text-gray-700">
                      Total: R {option.total}{' '}
                      <span className="text-sm text-gray-500">(incl VAT)</span>
                    </div>
                  </div>
                </div>

                <Button
                  variant="primary"
                  onClick={() => handleMembershipSelect(option.id)}
                  disabled={!isMemberTypeReady || isUpdating || isCalculatingFees}
                  isLoading={isUpdating || isCalculatingFees}
                >
                  {isUpdating || isCalculatingFees ? 'Processing...' : 'Choose'}
                  {!isUpdating && !isCalculatingFees && <ChevronRight />}
                </Button>
              </div>
            ))}
          </div>

          {!isMemberTypeReady && (
            <p className="text-center text-gray-500 mt-6">
              Loading membership type, please wait...
            </p>
          )}
        </div>
      </div>
    </Card>
  );
};

export default Membership;
